/** @jsx jsx */
import { css, jsx } from '@emotion/react'
import React, { useState } from 'react'
import { Icon } from '../../utils/Icon'
import { VStack } from './VStack'
import { Horizon } from '../map_facilities/Horizon'
import { ButtonLink } from './ButtonLink'
import { CardSection, Heading } from './CardSection'
import { CategoryHelpDialog } from './CategoryHelpDialog'
import { FacilityMedicalInstitutionType } from '../../entites/FacilityInfo'
import { getMedicalInstitutionTypeLabel } from '../../utils/getMedicalInstitutionTypeLabel'

export type CheckpointsProps = {
  childCategory: string
  minAge: string
  maxAge: string
  areas: string[]
  reservablePriorToRegistrationApproval: string
  lunchType: string
  cancelDeadline: string
  cancelPrice: string
  startGuidePageUrl: string
  pricingPageUrl: string
  termUrl: string | null
  medicalInstitutionType: FacilityMedicalInstitutionType
}

export const Checkpoints = ({
  childCategory,
  minAge,
  maxAge,
  areas,
  reservablePriorToRegistrationApproval,
  lunchType,
  medicalInstitutionType,
  cancelDeadline,
  cancelPrice,
  startGuidePageUrl,
  pricingPageUrl,
  termUrl,
}: CheckpointsProps) => {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <CardSection css={checkpointsStyle}>
      <Heading>ご利用前のチェックポイント</Heading>
      <VStack
        css={css`
          & > :nth-child(even) {
            padding-left: 19px;
          }
        `}
      >
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>🤒</span>
          <b>受け入れ可能な子どもの体調</b>
        </Horizon>
        <Horizon
          columnGap="5px"
          display="flex"
          onClick={() => setIsOpen(true)}
          css={css`
            cursor: pointer;
          `}
        >
          {childCategory}
          <Icon
            iconName="help_gray"
            style={{
              height: '14px',
              width: '14px',
            }}
          />
        </Horizon>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>👶</span>
          <b>受け入れ対象年齢</b>
        </Horizon>
        <Horizon columnGap="3px" display="flex">
          {minAge}
          <span>～</span>
          {maxAge}
        </Horizon>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>📍</span>
          <b>受け入れ対象地域</b>
        </Horizon>
        <ul>
          {areas.map((area) => (
            <li key={area}>{area}</li>
          ))}
        </ul>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>💦</span>
          <b>登録承認前の予約</b>
        </Horizon>
        <span>{reservablePriorToRegistrationApproval}</span>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>🍽</span>
          <b>昼食</b>
        </Horizon>
        <span>{lunchType}</span>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>🏥</span>
          <b>入室前に受診が必要な医療機関</b>
        </Horizon>
        <span>{getMedicalInstitutionTypeLabel(medicalInstitutionType)}</span>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>⏰</span>
          <b>キャンセル締め切り</b>
        </Horizon>
        <span>{cancelDeadline}</span>
        <Horizon columnGap="5px" display="flex" as="h4">
          <span>👋</span>
          <b>キャンセル料金</b>
        </Horizon>
        <span>{cancelPrice}</span>
      </VStack>
      <hr color="#EDECEB" style={{ margin: '25px -20px 0px', height: '1px' }} />
      {termUrl && (
        <Horizon
          columnGap="2px"
          justifyContent="center"
          style={{ marginTop: '25px' }}
        >
          <Icon
            iconName="help_black"
            style={{
              height: '14px',
              width: '14px',
            }}
          />
          <a href={termUrl} className="link">
            この施設の利用規約を確認する
          </a>
        </Horizon>
      )}
      <VStack gap="20px" style={{ marginTop: '25px' }}>
        <ButtonLink
          href={startGuidePageUrl}
          mainColor="black"
          variant="outlined"
          borderWidth="1px"
        >
          ご利用方法・持ち物
        </ButtonLink>
        <ButtonLink
          href={pricingPageUrl}
          mainColor="black"
          variant="outlined"
          borderWidth="1px"
        >
          料金一覧
        </ButtonLink>
      </VStack>
      <CategoryHelpDialog isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </CardSection>
  )
}

const checkpointsStyle = css`
  & h4 {
    margin-top: 15px;
    margin-bottom: 3px;

    &:first-of-type {
      margin-top: 0;
    }
  }

  & ul > li {
    position: relative;
  }

  .link {
    font-size: 12px;
    line-height: 150%;
    text-align: center;
    text-decoration-line: underline;

    &:visited,
    &:link {
      color: inherit;
    }
  }
`
